import React from 'react';

import { Field } from './Field';
import { VisibilityField } from './VisibilityField';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  LENS_ALIAS_HELP,
  LENS_ALIAS_MAX_LENGTH,
  LENS_ALIAS_PLACEHOLDER,
  LENS_NAME_MAX_LENGTH,
  LENS_NAME_PLACEHOLDER,
  type LensFieldErrors,
} from '../lib/lensForm';

export interface LensBasicsValue {
  name: string;
  alias: string;
  description: string;
  isPublic: boolean;
}

interface LensBasicsFieldsProps {
  value: LensBasicsValue;
  /** The caller clears that key's error (DESIGN_SYSTEM.md §5 Validation). */
  onChange: <K extends keyof LensBasicsValue>(key: K, next: LensBasicsValue[K]) => void;
  errors: LensFieldErrors;
  /**
   * `edit`: the Public lens checkbox. `readOnly`: the badge and sentence (an
   * existing lens, without lenses:make_public). `hidden`: nothing (a new lens
   * without it, which is forced private).
   */
  visibility: 'edit' | 'readOnly' | 'hidden';
  disabled?: boolean;
  /** A refusal about visibility itself (a public lens holding a private template). */
  visibilityError?: string | null;
}

/**
 * A lens's name, alias, description and visibility — the same controls, order
 * and copy on the Lenses page, the publish wizard's Create lens and System
 * Admin → All Lenses (DESIGN_SYSTEM.md §9 "One field, one control"). The rules
 * and their copy live in lib/lensForm.ts; the organization and template pickers
 * stay in each dialog shell.
 */
export const LensBasicsFields: React.FC<LensBasicsFieldsProps> = ({
  value,
  onChange,
  errors,
  visibility,
  disabled = false,
  visibilityError,
}) => (
  <div className="space-y-4">
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Name" required error={errors.name}>
        {(control) => (
          <Input
            {...control}
            value={value.name}
            maxLength={LENS_NAME_MAX_LENGTH}
            autoComplete="off"
            disabled={disabled}
            placeholder={LENS_NAME_PLACEHOLDER}
            onChange={(event) => onChange('name', event.target.value)}
          />
        )}
      </Field>
      <Field label="Alias" required error={errors.alias} help={LENS_ALIAS_HELP}>
        {(control) => (
          <Input
            {...control}
            mono
            value={value.alias}
            maxLength={LENS_ALIAS_MAX_LENGTH}
            autoComplete="off"
            disabled={disabled}
            placeholder={LENS_ALIAS_PLACEHOLDER}
            onChange={(event) => onChange('alias', event.target.value)}
          />
        )}
      </Field>
    </div>

    <Field label="Description">
      {(control) => (
        <Textarea
          {...control}
          rows={3}
          value={value.description}
          disabled={disabled}
          onChange={(event) => onChange('description', event.target.value)}
        />
      )}
    </Field>

    {visibility === 'edit' ? (
      <VisibilityField
        kind="lens"
        checked={value.isPublic}
        disabled={disabled}
        error={visibilityError}
        onCheckedChange={(checked) => onChange('isPublic', checked)}
      />
    ) : visibility === 'readOnly' ? (
      <VisibilityField kind="lens" readOnly checked={value.isPublic} />
    ) : null}
  </div>
);
